import { useEventCallback } from "usehooks-ts";

import { type JSX, MouseEvent, ReactNode } from "react";

import DialogContent from "@mui/material/DialogContent";
import Popover, { PopoverProps } from "@mui/material/Popover";
import { Breakpoint, styled } from "@mui/material/styles";

export interface StandardPopoverProps {
  isOpen: boolean;
  onClose?: PopoverProps["onClose"];
  /**
   * @deprecated Use {@link onClose} instead.
   * @param value
   * @returns
   */
  setIsOpen?: (value: boolean) => void;
  anchorElement: Element | null;
  children: ReactNode;
  closeButton?: ReactNode;
  /**
   * maxWidth for DialogContent, similar to MUI Dialog. Accepts false, 'xs', 'sm', 'md', 'lg', 'xl'
   */
  maxWidth?: Breakpoint;
}

export type StandardPopoverContentProps = {
  /**
   * maxWidth for DialogContent, similar to MUI Dialog. Accepts false, 'xs', 'sm', 'md', 'lg', 'xl'
   */
  maxWidth?: Breakpoint;
};

export const StandardPopoverContent = styled(DialogContent, {
  shouldForwardProp: (prop) => prop !== "maxWidth",
})<StandardPopoverContentProps>(({ theme, maxWidth }) => ({
  maxWidth: maxWidth != null ? theme.breakpoints.values[maxWidth] : undefined,
}));

export function CustomStandardPopover(
  props: StandardPopoverProps,
): JSX.Element {
  const {
    isOpen,
    setIsOpen,
    onClose,
    maxWidth,
    closeButton,
    anchorElement,
    children,
  } = props;

  const handleClose = useEventCallback(() => {
    setIsOpen?.(false);
  });

  return (
    <Popover
      open={isOpen}
      onClose={onClose ?? handleClose}
      anchorEl={anchorElement}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      {closeButton}
      {/* DialogContent is intended for dialogs, but seems to work with popovers as well */}
      {/* Custom padding added */}
      <StandardPopoverContent maxWidth={maxWidth} sx={{ padding: "0rem 1rem" }}>
        {children}
      </StandardPopoverContent>
    </Popover>
  );
}

function stopPropagation(e: MouseEvent): void {
  e.stopPropagation();
}
